'use client';

import {
  type ConvictionMarketFields,
  outcomeConvictionFraction,
} from '@morfi/backend-sdk-v2/client';

interface OutcomeData {
  outcomeIndex: number;
  twarValue: string;
}

const EMPTY_MAP: ReadonlyMap<number, number> = new Map();

/**
 * Maps each outcome's RAW conviction (TWAR) to a normalized market share —
 * `conviction = outcome.twarValue / 1e9` (D9 PPB → fraction).
 *
 * Under the normalized model the targets sum to ~1e9 PPB, so the per-outcome
 * EMA values converge to fractions (0 → ~1.0) that collectively sum to ~1.0.
 * An outcome resolves when its share crosses the configured threshold θ (≤ 1.0).
 * Do NOT re-normalise by the cross-outcome sum — the raw value IS the share
 * (within dust); any further division would introduce rounding error and
 * undermine the exact normalized winner check in the contract.
 *
 * Legacy gauge-era markets (before normalization) may transiently carry
 * per-outcome values > 1.0 while decaying toward the new targets; display must
 * not clamp these — they are valid wire values that decay away within ~1 hour.
 *
 * The backend delivers the authoritative decayed conviction via CDC; this
 * just converts the wire PPB string to a display fraction.
 */
export function useOutcomeConviction(
  outcomes: ReadonlyArray<OutcomeData> | undefined,
  market: ConvictionMarketFields = {},
): ReadonlyMap<number, number> {
  if (outcomes === undefined || outcomes.length === 0) return EMPTY_MAP;

  const result = new Map<number, number>();
  for (const o of outcomes) {
    result.set(
      Number(o.outcomeIndex),
      outcomeConvictionFraction({ outcomeIndex: o.outcomeIndex, conviction: o.twarValue }, market),
    );
  }
  return result;
}
